import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, SlidersHorizontal, X, Grid3X3, LayoutList } from 'lucide-react';
import { ProductCard, TechLabel } from '../components/common/TacticalCard';
import { productsApi, categoriesApi, technologiesApi } from '../lib/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { staggerContainer, fadeUpVariant } from '../lib/animations';
import SEO from '../components/common/SEO';

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  // Get filter values from URL
  const category = searchParams.get('category') || '';
  const technology = searchParams.get('tech') || '';
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1');

  // Fetch products
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 12, sort };
        if (category) params.category = category;
        if (search) params.search = search;

        const response = await productsApi.getAll(params);
        let fetchedProducts = response.data.products;
        
        // Filter by technology if needed
        if (technology && fetchedProducts.length > 0) {
          fetchedProducts = fetchedProducts.filter(p => 
            p.technologies && p.technologies.includes(technology)
          );
        }
        
        setProducts(fetchedProducts);
        setTotalProducts(response.data.total);
        setTotalPages(response.data.pages);
      } catch (err) {
        console.error('Error fetching products:', err);
        setProducts(getMockProducts());
        setTotalProducts(8);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, technology, search, sort, page]);

  // Fetch categories and technologies
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [catRes, techRes] = await Promise.all([
          categoriesApi.getAll(),
          technologiesApi.getAll()
        ]);
        setCategories(catRes.data.categories || []);
        setTechnologies(techRes.data.technologies || []);
      } catch (err) {
        // Use mock data
        setCategories([
          { category_id: '1', name: 'Pantolonlar', slug: 'pantolon' },
          { category_id: '2', name: 'Montlar', slug: 'mont' },
          { category_id: '3', name: 'Gömlekler', slug: 'gomlek' },
          { category_id: '4', name: 'Tişörtler', slug: 'tshirt' },
          { category_id: '5', name: 'Aksesuarlar', slug: 'aksesuar' },
        ]);
        setTechnologies([
          { tech_id: '1', name: 'Su İtici', slug: 'su-itici' },
          { tech_id: '2', name: 'Nefes Alabilir', slug: 'nefes-alabilir' },
          { tech_id: '3', name: 'Ripstop', slug: 'ripstop' },
        ]);
      }
    };
    fetchFilters();
  }, []);

  // Mock products
  const getMockProducts = () => [
    {
      product_id: '1',
      slug: 'tactical-pantolon-hidden13',
      name: 'Tactical Pantolon Dayanıklı Rahat HIDDEN13',
      price: 999,
      original_price: 3329,
      discount_percent: 70,
      images: [
        'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=400&q=80',
        'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: false,
      category: 'pantolon',
      variants: [
        { size: 'M', color: 'Siyah', color_code: '#1a1a1a' },
        { size: 'L', color: 'Haki', color_code: '#6b7c5c' },
      ],
      technologies: ['su-itici', 'ripstop'],
    },
    {
      product_id: '2',
      slug: '3in1-outdoor-parka-3stro01',
      name: '3IN1 Outdoor Tactical Su Geçirmez Erkek Parka',
      price: 1999,
      original_price: 6663,
      discount_percent: 70,
      images: [
        'https://images.unsplash.com/photo-1544923246-77307dd628b3?w=400&q=80',
        'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: true,
      category: 'mont',
      variants: [
        { size: 'L', color: 'Siyah', color_code: '#1a1a1a' },
      ],
      technologies: ['su-itici', 'termal-yalitim'],
    },
    {
      product_id: '3',
      slug: 'crab-ranger-pantolon-ranger11',
      name: 'CRAB RANGER PANTOLON RANGER11',
      price: 749,
      original_price: 2499,
      discount_percent: 70,
      images: [
        'https://images.unsplash.com/photo-1473966968600-fa801b869a1a?w=400&q=80',
        'https://images.unsplash.com/photo-1517445312882-bc9910d016b7?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: true,
      category: 'pantolon',
      variants: [
        { size: 'M', color: 'Haki', color_code: '#6b7c5c' },
        { size: 'L', color: 'Siyah', color_code: '#1a1a1a' },
      ],
      technologies: ['cordura', 'su-itici'],
    },
    {
      product_id: '4',
      slug: 'tactical-gomlek-tacflex01',
      name: 'Tactical Outdoor Gömlek Esnek Fit TACFLEX01',
      price: 1499,
      original_price: 2599,
      discount_percent: 42,
      images: [
        'https://images.unsplash.com/photo-1598033129183-c4f50c736f10?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: false,
      category: 'gomlek',
      variants: [
        { size: 'M', color: 'Haki', color_code: '#6b7c5c' },
      ],
      technologies: ['nefes-alabilir', '4-yon-strec'],
    },
    {
      product_id: '5',
      slug: 'sirt-cantasi-crabwalk01',
      name: 'SIRT ÇANTASI CRABWALK01',
      price: 899,
      original_price: 2996,
      discount_percent: 70,
      images: [
        'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: true,
      category: 'aksesuar',
      variants: [
        { size: 'Tek Ebat', color: 'Siyah', color_code: '#1a1a1a' },
        { size: 'Tek Ebat', color: 'Haki', color_code: '#6b7c5c' },
      ],
      technologies: ['cordura', 'su-itici'],
    },
    {
      product_id: '6',
      slug: 'outdoor-tactical-mont-kolt30',
      name: 'Outdoor Tactical Kışlık Erkek Montu KOLT30',
      price: 1999,
      original_price: 6663,
      discount_percent: 70,
      images: [
        'https://images.unsplash.com/photo-1551028719-00167b16eac5?w=400&q=80'
      ],
      brand: 'VAVWEAR',
      is_new: false,
      category: 'mont',
      variants: [
        { size: 'XL', color: 'Siyah', color_code: '#1a1a1a' },
      ],
      technologies: ['su-itici', 'termal-yalitim'],
    },
  ];

  // Update URL params
  const updateFilters = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== 'page') {
      newParams.delete('page');
    }
    setSearchParams(newParams);
  };

  // Category display names
  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.name || slug;
  };

  // Filter products
  const displayProducts = products.length > 0 ? products : getMockProducts();

  // SEO title and description based on filters
  const getSeoTitle = () => {
    if (category) {
      const catName = getCategoryName(category);
      return `${catName} | Mağaza`;
    }
    if (search) return `"${search}" Arama Sonuçları`;
    return 'Mağaza | Tüm Ürünler';
  };

  const getSeoDescription = () => {
    if (category) {
      const catName = getCategoryName(category);
      return `VAV Wear ${catName} kategorisinde ${totalProducts} ürün. Premium taktik ve outdoor giyim koleksiyonunu keşfedin.`;
    }
    return `VAV Wear mağazasında ${totalProducts} premium taktik ve outdoor ürün. Askeri standartlarda kalite, profesyonel performans.`;
  };

  return (
    <motion.main 
      className="min-h-screen bg-vav-bg pt-20" 
      data-testid="shop-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* SEO Meta Tags */}
      <SEO 
        title={getSeoTitle()}
        description={getSeoDescription()}
      />
      
      {/* Header */}
      <div className="bg-vav-bg-paper border-b border-vav-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div 
            className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Title & Breadcrumb */}
            <div>
              <div className="flex items-center gap-2 text-sm text-vav-fg-muted mb-2">
                <Link to="/" className="hover:text-vav-accent-cyan transition-colors">Ana Sayfa</Link>
                <span>/</span>
                <span className="text-vav-fg">Mağaza</span>
                {category && (
                  <>
                    <span>/</span>
                    <span className="text-vav-accent-cyan">{getCategoryName(category)}</span>
                  </>
                )}
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl text-vav-fg">
                {category ? getCategoryName(category) : 'TÜM ÜRÜNLER'}
              </h1>
              <p className="text-vav-fg-muted mt-1">{totalProducts} ürün</p>
            </div>

            {/* Search */}
            <div className="w-full lg:w-auto flex items-center gap-4">
              <div className="relative flex-1 lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vav-fg-muted" />
                <input
                  type="text"
                  placeholder="Ürün ara..."
                  value={search}
                  onChange={(e) => updateFilters('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-vav-bg border border-vav-border text-vav-fg placeholder:text-vav-fg-dark focus:border-vav-accent-cyan focus:outline-none rounded-lg"
                  data-testid="search-input"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-8">
              {/* Categories */}
              <div>
                <h3 className="font-heading text-lg text-vav-fg mb-4 uppercase">Kategoriler</h3>
                <ul className="space-y-1">
                  <li>
                    <button
                      onClick={() => updateFilters('category', '')}
                      className={`w-full text-left py-2 px-3 rounded-lg text-sm transition-colors ${
                        !category ? 'bg-vav-bg-subtle text-vav-accent-cyan' : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle/50'
                      }`}
                      data-testid="filter-all"
                    >
                      Tüm Ürünler
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.category_id}>
                      <button
                        onClick={() => updateFilters('category', cat.slug)}
                        className={`w-full text-left py-2 px-3 rounded-lg text-sm transition-colors ${
                          category === cat.slug ? 'bg-vav-bg-subtle text-vav-accent-cyan' : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle/50'
                        }`}
                        data-testid={`filter-${cat.slug}`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technologies */}
              <div>
                <h3 className="font-heading text-lg text-vav-fg mb-4 uppercase">Teknoloji</h3>
                <ul className="space-y-1">
                  {technologies.map((tech) => (
                    <li key={tech.tech_id}>
                      <button
                        onClick={() => updateFilters('tech', technology === tech.slug ? '' : tech.slug)}
                        className={`w-full text-left py-2 px-3 rounded-lg text-sm transition-colors ${
                          technology === tech.slug ? 'bg-vav-bg-subtle text-vav-accent-cyan' : 'text-vav-fg-muted hover:text-vav-fg hover:bg-vav-bg-subtle/50'
                        }`}
                      >
                        {tech.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b border-vav-border">
              {/* Mobile Filter Toggle */}
              <button
                onClick={() => setFiltersOpen(true)}
                className="lg:hidden flex items-center gap-2 px-4 py-2 border border-vav-border text-vav-fg hover:border-vav-accent-cyan transition-colors rounded-lg"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filtreler
              </button>

              {/* Sort */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-vav-fg-muted hidden sm:block">Sırala:</span>
                <Select value={sort} onValueChange={(value) => updateFilters('sort', value)}>
                  <SelectTrigger className="w-40 bg-vav-bg border-vav-border text-vav-fg rounded-lg" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-vav-bg-paper border-vav-border">
                    <SelectItem value="newest">En Yeni</SelectItem>
                    <SelectItem value="price_asc">Fiyat (Artan)</SelectItem>
                    <SelectItem value="price_desc">Fiyat (Azalan)</SelectItem>
                    <SelectItem value="name_asc">İsim (A-Z)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View Mode */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'grid' ? 'text-vav-accent-cyan bg-vav-bg-subtle' : 'text-vav-fg-muted hover:text-vav-fg'}`}
                  data-testid="view-grid"
                >
                  <Grid3X3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${viewMode === 'list' ? 'text-vav-accent-cyan bg-vav-bg-subtle' : 'text-vav-fg-muted hover:text-vav-fg'}`}
                  data-testid="view-list"
                >
                  <LayoutList className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Active Filters */}
            {(category || technology || search) && (
              <motion.div 
                className="flex flex-wrap items-center gap-2 mb-6"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span className="text-sm text-vav-fg-muted">Aktif filtreler:</span>
                {category && (
                  <TechLabel variant="accent" className="cursor-pointer" onClick={() => updateFilters('category', '')}>
                    {getCategoryName(category)}
                    <X className="w-3 h-3 ml-1" />
                  </TechLabel>
                )}
                {technology && (
                  <TechLabel variant="accent" className="cursor-pointer" onClick={() => updateFilters('tech', '')}>
                    {technologies.find(t => t.slug === technology)?.name || technology}
                    <X className="w-3 h-3 ml-1" />
                  </TechLabel>
                )}
                {search && (
                  <TechLabel variant="accent" className="cursor-pointer" onClick={() => updateFilters('search', '')}>
                    "{search}"
                    <X className="w-3 h-3 ml-1" />
                  </TechLabel>
                )}
              </motion.div>
            )}

            {/* Products */}
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div 
                  className="grid gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {[1, 2, 3, 4, 5, 6].map((i) => (
                    <div key={i} className="aspect-[3/4] bg-vav-bg-paper rounded-2xl animate-pulse" />
                  ))}
                </motion.div>
              ) : displayProducts.length === 0 ? (
                <motion.div 
                  className="text-center py-16"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <p className="text-vav-fg-muted text-lg mb-4">Ürün bulunamadı</p>
                  <button
                    onClick={() => setSearchParams(new URLSearchParams())}
                    className="tactical-btn-outline"
                  >
                    Filtreleri Temizle
                  </button>
                </motion.div>
              ) : (
                <motion.div 
                  className="grid gap-6 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
                  variants={staggerContainer}
                  initial="initial"
                  animate="enter"
                >
                  {displayProducts.map((product, index) => (
                    <ProductCard key={product.product_id} product={product} index={index} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <motion.button
                    key={p}
                    onClick={() => updateFilters('page', p.toString())}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg border transition-colors ${
                      p === page 
                        ? 'border-vav-accent-cyan text-vav-accent-cyan bg-vav-accent-cyan/10' 
                        : 'border-vav-border text-vav-fg-muted hover:border-vav-fg'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    data-testid={`page-${p}`}
                  >
                    {p}
                  </motion.button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <AnimatePresence>
        {filtersOpen && (
          <>
            <motion.div 
              className="fixed inset-0 bg-black/80 z-50 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFiltersOpen(false)}
            />
            <motion.div 
              className="fixed right-0 top-0 bottom-0 w-80 bg-vav-bg-paper p-6 overflow-y-auto z-50 lg:hidden"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl text-vav-fg uppercase">Filtreler</h3>
                <button onClick={() => setFiltersOpen(false)} className="p-2 text-vav-fg-muted hover:text-vav-fg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Categories */}
              <div className="mb-8">
                <h4 className="font-heading text-sm text-vav-fg-muted mb-3 uppercase">Kategoriler</h4>
                <ul className="space-y-1">
                  <li>
                    <button
                      onClick={() => { updateFilters('category', ''); setFiltersOpen(false); }}
                      className={`w-full text-left py-2 px-3 rounded-lg text-sm ${!category ? 'text-vav-accent-cyan bg-vav-bg-subtle' : 'text-vav-fg-muted'}`}
                    >
                      Tüm Ürünler
                    </button>
                  </li>
                  {categories.map((cat) => (
                    <li key={cat.category_id}>
                      <button
                        onClick={() => { updateFilters('category', cat.slug); setFiltersOpen(false); }}
                        className={`w-full text-left py-2 px-3 rounded-lg text-sm ${category === cat.slug ? 'text-vav-accent-cyan bg-vav-bg-subtle' : 'text-vav-fg-muted'}`}
                      >
                        {cat.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Technologies */}
              <div>
                <h4 className="font-heading text-sm text-vav-fg-muted mb-3 uppercase">Teknoloji</h4>
                <ul className="space-y-1">
                  {technologies.map((tech) => (
                    <li key={tech.tech_id}>
                      <button
                        onClick={() => { updateFilters('tech', technology === tech.slug ? '' : tech.slug); setFiltersOpen(false); }}
                        className={`w-full text-left py-2 px-3 rounded-lg text-sm ${technology === tech.slug ? 'text-vav-accent-cyan bg-vav-bg-subtle' : 'text-vav-fg-muted'}`}
                      >
                        {tech.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.main>
  );
};

export default Shop;
